import { useCallback } from 'react';
import { Col, Row } from 'react-bootstrap';
import { SubmitHandler, useForm } from 'react-hook-form';

import { Stack } from 'apps/dashboard/shared/scripts/components';
import { useYupValidation } from 'apps/shared/scripts/hooks';

import CardPreview from '../CardPreview';
import CardShare from '../CardShare';
import CardStats from '../CardStats';
import Savebar from '../Savebar';

import CardCreateSuccess from './CardCreateSuccess';
import CardDelivery from './CardDelivery';
import CardDesign from './CardDesign';
import CardDetails from './CardDetails';
import { DEFAULT_VALUES } from './constants';
import { FormValues } from './types';
import { schema } from './validation';

export interface Props {
  newCardId?: string;
  defaultValues?: FormValues;
  isEditMode?: boolean;
  isSubmitting?: boolean;
  isSuccess?: boolean;
  onSubmit: (data: FormValues) => Promise<void>;
}

const CardForm = ({
  newCardId,
  defaultValues = DEFAULT_VALUES,
  isEditMode,
  isSubmitting,
  isSuccess,
  onSubmit,
}: Props) => {
  const {
    control,
    formState: { errors, isDirty },
    handleSubmit,
    reset,
    watch,
  } = useForm<FormValues>({
    defaultValues,
    resolver: useYupValidation<FormValues>(schema),
  });
  const formValues = watch();

  const onReset = useCallback(() => {
    // wrap in callback to prevent maximum call stack error
    reset();
  }, [reset]);

  const onSubmitHandler: SubmitHandler<FormValues> = useCallback(
    async (data) => {
      try {
        await onSubmit(data);
        setTimeout(() => reset(formValues), 500);
      } catch {
        // no-op
      }
    },
    [formValues, onSubmit, reset],
  );

  return (
    <>
      <Row>
        <Col lg={7}>
          <Stack>
            {newCardId ? (
              <CardCreateSuccess cardId={newCardId} />
            ) : (
              <form onSubmit={handleSubmit(onSubmitHandler)}>
                {isEditMode && (
                  <>
                    <CardStats />
                    <CardShare />
                  </>
                )}
                <CardDetails control={control} errors={errors} />
                <CardDesign control={control} errors={errors} />
                <CardDelivery control={control} errors={errors} />
              </form>
            )}
          </Stack>
        </Col>
        <Col lg={5}>
          <CardPreview formValues={formValues as FormValues} />
        </Col>
      </Row>
      <Savebar
        onDiscard={onReset}
        onSave={handleSubmit(onSubmitHandler)}
        isLoading={isSubmitting}
        isSuccess={isSuccess}
        isVisible={isDirty}
        isEditMode={isEditMode}
      />
    </>
  );
};

CardForm.displayName = 'CardForm';

export default CardForm;
